/**
 * Messages and notifications, as icons with counters in the header (the
 * user, 2026-10-07) — no longer rows in the main menu. Each opens a DROPDOWN
 * with the most recent items and "View all", which opens the full page. The
 * same two are in the account menu with their counts (`InboxMenuItems`),
 * which is where they live on a narrow phone when nothing is waiting.
 *
 * The counts are `GET /me/unread`: a message is unread until its recipient
 * opens the conversation; a notification until it is read. Asked every 30 s
 * and when the network comes back, only for a signed-in, approved
 * person — a guest has neither, and an unapproved account is refused.
 *
 * **Inside a rave the bell is that rave's** (step 61, §37.14, §37.80): its
 * count, its list, its pop-ups and its "Mark all read" — `?editionId=` of the
 * rave on screen (`useCurrentRave`). Outside, all of them. Messages are always
 * all: a conversation belongs to the main app.
 *
 * Nothing here imports the dating or messages SCREENS: this is in the shell,
 * which every page downloads first. Their lists are read by the same query
 * keys, so a dropdown and the page share one cache.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type NotificationPage, type NotificationRow } from '@/lib/api';
import { readJson, writeJson } from '@/lib/storage';
import { accountUrl, appUrl, isAppHost } from '@/lib/tenant';
import { useCurrentRave } from '@/lib/currentRave';
import { NOTIFICATION_CATEGORIES, notificationCategory, type NotificationCategory } from '@myrave/shared';
import { useT } from '@/i18n';
import { useAuth, leaveTo } from './AuthProvider';
import { FeedEmergency, isCritical, useEmergencyHeld } from './EmergencyScreen';
import '@/admin/fonts/fonts.css';
import './HeaderInbox.css';

export interface Unread { notifications: number; messages: number }

const UNREAD_KEY = ['me', 'unread'] as const;
/** The dating screen's and the messages page's own keys — one cache for all three. */
const MATCHES_KEY = ['dating', 'matches'] as const;
const RIDE_CHATS_KEY = ['ride-chats'] as const;
const RECENT = 5;

/** `&editionId=` / `?editionId=` of the rave on screen, or nothing outside one. */
function useRaveScope(): string | null {
  return useCurrentRave()?.editionId ?? null;
}
/** What the bell's list is asked for: a rave (or all), words (§37.84), the expired ones too (§37.59). */
interface ListAsk { editionId: string | null; q?: string; history?: boolean }
const listPath = ({ editionId, q, history }: ListAsk, limit: number, cursor?: string) =>
  `/me/notifications?limit=${limit}`
  + (editionId ? `&editionId=${encodeURIComponent(editionId)}` : '')
  + (q ? `&q=${encodeURIComponent(q)}` : '')
  + (history ? '&history=true' : '')
  + (cursor ? `&cursor=${encodeURIComponent(cursor)}` : '');
const listKey = ({ editionId, q, history }: ListAsk) =>
  ['me', 'notifications', 'recent', editionId ?? 'all', ...(q ? ['q', q] : []), ...(history ? ['history'] : [])];
const recentPath = (editionId: string | null) => listPath({ editionId }, RECENT);
const recentKey = (editionId: string | null) => listKey({ editionId });

function useUnreadFor(scope: string | null): Unread | null {
  const { user } = useAuth();
  const allowed = Boolean(user && (user.approved || user.role === 'SUPER_ADMIN'));
  const q = useQuery({
    queryKey: [...UNREAD_KEY, scope ?? 'all'],
    queryFn: () => api.get<Unread>(scope ? `/me/unread?editionId=${encodeURIComponent(scope)}` : '/me/unread'),
    enabled: allowed,
    // Every 30 s: a new notification pops up (below), and a minute late read as broken.
    refetchInterval: 30_000,
    // Back online, ask at once — what was missed arrives (§37.50).
    refetchOnReconnect: 'always',
    staleTime: 15_000,
    retry: false,
  });
  return allowed ? (q.data ?? { notifications: 0, messages: 0 }) : null;
}

/** The counters on the icons — inside a rave, the bell's is that rave's. */
export function useUnread(): Unread | null {
  return useUnreadFor(useRaveScope());
}

/**
 * Every rave's together, wherever this page is: the bell's "N more outside
 * this rave" and the full-screen emergency, which no rave scope may hide.
 * Outside a rave it IS `useUnread`'s query — one key, one request.
 */
function useUnreadAll(): Unread | null {
  return useUnreadFor(null);
}

/** `value`, once it has stood still for `ms`. */
export function useSettled(value: string, ms: number): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

/** Where each full page is: the app's own conversations, and the account's notifications. */
const messagesUrl = (query = '') => appUrl(`/messages${query}`);
const NOTIFICATIONS = () => accountUrl('/notifications');
/** Every notification, on its own page under the account (w-notif-bell-feed). */
export const ALL_NOTIFICATIONS = () => accountUrl('/notifications?view=all');
/** Opens a notification's own address — the page it is about (the bell's row, a pop-up, the account's list). */
export function openNotificationUrl(url: string): void {
  open(url);
}
function open(url: string): void {
  // On the app host /messages is an in-app route; anywhere else it is a hop.
  if (isAppHost() && new URL(url).host === window.location.host) window.location.assign(new URL(url).pathname + new URL(url).search);
  else void leaveTo(url);
}

const ICON = {
  messages: 'M4 5h16v11H8l-4 4z',
  notifications: 'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 0 0 3.4 0',
};

/** "5 min", "3 h", "2 d" — how long ago, short enough for a dropdown row. */
export function ago(iso: string, tr: ReturnType<typeof useT>): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return tr('now');
  if (s < 3600) return tr('{count} min', { count: Math.floor(s / 60) });
  if (s < 86_400) return tr('{count} h', { count: Math.floor(s / 3600) });
  return tr('{count} d', { count: Math.floor(s / 86_400) });
}

function Count({ n }: { n: number }) {
  if (n <= 0) return null;
  return <span className="header-inbox-count" aria-hidden="true">{n > 99 ? '99+' : n}</span>;
}

/* ---- the two dropdowns ---------------------------------------------------- */

/**
 * The notification HISTORY is this dropdown (the user, 2026-10-07: "the
 * history already exists in the bell icon") — the newest first, and "Show
 * older" pages back on the server's cursor inside it. The account's
 * Notifications section is settings only; its link is at the foot.
 */
function NotificationsPanel({ onDone, outside, here }: {
  onDone: () => void;
  /** Unread notifications of OTHER raves and none — "N more outside this rave". */
  outside: number;
  /** This rave's unread — the "This rave" pill's count (the whole bell's outside a rave). */
  here: number;
}) {
  const tr = useT();
  const qc = useQueryClient();
  const rave = useRaveScope();
  /*
   * Inside a rave: "This rave" (the default) or "All" (the user's decision,
   * step 61) — All asks, lists and marks with no rave at all. The icon's
   * counter stays the rave's either way.
   */
  const [scope, setScope] = useState<'rave' | 'all'>('rave');
  const editionId = rave && scope === 'rave' ? rave : null;
  // Search after a pause, from two letters (§37.84).
  const [typed, setTyped] = useState('');
  const settled = useSettled(typed.trim(), 300);
  const q = settled.length >= 2 ? settled : '';
  // The expired ones too — the history (§37.59).
  const [history, setHistory] = useState(false);
  const ask: ListAsk = { editionId, q, history };
  const askKey = JSON.stringify(ask);

  const recent = useQuery({
    queryKey: listKey(ask),
    queryFn: () => api.get<NotificationPage>(listPath(ask, RECENT)),
    staleTime: 15_000,
    retry: false,
  });
  // "Show older" pages on what is asked now; a new question starts again.
  const [older, setOlder] = useState<{ ask: string; rows: NotificationRow[]; cursor: string | null } | null>(null);
  const olderHere = older?.ask === askKey ? older : null;
  const [loadingOlder, setLoadingOlder] = useState(false);
  const cursor = olderHere ? olderHere.cursor : recent.data?.nextCursor ?? null;
  const showOlder = async () => {
    if (!cursor) return;
    setLoadingOlder(true);
    try {
      const page = await api.get<NotificationPage>(listPath(ask, 10, cursor));
      setOlder((o) => ({ ask: askKey, rows: [...(o?.ask === askKey ? o.rows : []), ...page.notifications], cursor: page.nextCursor }));
    } finally {
      setLoadingOlder(false);
    }
  };
  const markRead = useMutation({
    // "Mark all read" marks what the bell shows: this rave's, or all.
    mutationFn: (ids?: string[]) => api.post('/me/notifications/read', ids ? { ids } : editionId ? { editionId } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: UNREAD_KEY });
      void qc.invalidateQueries({ queryKey: ['me', 'notifications'] });
    },
  });
  // Deleted at once (§37.85); back if the server refused.
  const [deleted, setDeleted] = useState<string[]>([]);
  const remove = (id: string) => {
    setDeleted((d) => [...d, id]);
    api.del(`/me/notifications/${encodeURIComponent(id)}`).then(
      () => { void qc.invalidateQueries({ queryKey: UNREAD_KEY }); },
      () => setDeleted((d) => d.filter((x) => x !== id)),
    );
  };
  const all = [...(recent.data?.notifications ?? []), ...(olderHere?.rows ?? [])].filter((n) => !deleted.includes(n.id));
  // The design's chips: the kinds the list holds, each with its count — never an empty one.
  const [kind, setKind] = useState<NotificationCategory | null>(null);
  const kinds = NOTIFICATION_CATEGORIES.map((c) => ({ c, n: all.filter((r) => categoryOf(r) === c).length })).filter((k) => k.n > 0);
  const shownKind = kind && kinds.some((k) => k.c === kind) ? kind : null;
  const rows = shownKind ? all.filter((r) => categoryOf(r) === shownKind) : all;
  const unreadShown = (editionId ? recent.data?.unreadHere : recent.data?.unread) ?? 0;
  const raveName = useCurrentRave()?.name ?? null;
  return (
    <>
      <div className="inbox-sheet-head">
        <span className="inbox-sheet-title">
          <h2>{tr('Notifications')}</h2>
          {unreadShown > 0 && <span className="inbox-sheet-new">{tr('{count} new', { count: unreadShown })}</span>}
        </span>
        <span className="inbox-sheet-tools">
          {(unreadShown > 0 || all.some((n) => !n.readAt)) && (
            <button type="button" className="header-inbox-link inbox-sheet-markall" onClick={() => markRead.mutate(undefined)}>
              {tr('Mark all read')}
            </button>
          )}
          <CloseButton onClose={onDone} />
        </span>
      </div>
      {rave && (
        <div className="header-inbox-scope" role="radiogroup" aria-label={tr('Which notifications')}>
          {(['rave', 'all'] as const).map((s) => {
            const n = s === 'rave' ? here : here + outside;
            return (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={scope === s}
                className="header-inbox-scope-opt"
                data-keep-open
                onClick={() => setScope(s)}
              >
                <span className="header-inbox-scope-name">{s === 'rave' ? (raveName ? tr('This rave ({name})', { name: raveName }) : tr('This rave')) : tr('All')}</span>
                {n > 0 && <span className="header-inbox-scope-n">{n}</span>}
              </button>
            );
          })}
        </div>
      )}
      {rave && outside > 0 && (
        <div className="header-inbox-outside">{tr('{count} more outside this rave', { count: outside })}</div>
      )}
      <div className="inbox-sheet-find">
        <span className="inbox-search">
          <IconSearch />
          <input
            type="search"
            className="header-inbox-search"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={tr('Search notifications')}
            aria-label={tr('Search notifications')}
          />
        </span>
        {kinds.length > 1 && (
          <div className="inbox-chips" role="radiogroup" aria-label={tr('Kinds of notification')}>
            <button type="button" role="radio" aria-checked={!shownKind} className="inbox-chip" data-keep-open onClick={() => setKind(null)}>{tr('All')}</button>
            {kinds.map(({ c, n }) => (
              <button key={c} type="button" role="radio" aria-checked={shownKind === c} className="inbox-chip" data-keep-open onClick={() => setKind(c)}>
                {categoryWord(c, tr)} ({n})
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="inbox-sheet-list">
        {recent.isLoading && <div className="header-inbox-note">{tr('Loading…')}</div>}
        {recent.isError && <div className="header-inbox-note">{tr('Your notifications are not answering')}</div>}
        {!recent.isLoading && !recent.isError && rows.length === 0 && (
          q ? <div className="header-inbox-note">{tr('Nothing matches.')}</div> : (
            <div className="inbox-empty">
              <span className="inbox-empty-art" aria-hidden="true"><IconBellOff /><span className="inbox-empty-tick">✓</span></span>
              <h3 className="inbox-empty-title">{tr('Nothing new.')}</h3>
              <p className="inbox-empty-text">{tr('You’re all caught up — no changes, reminders or announcements right now.')}</p>
            </div>
          )
        )}
        {rows.map((n) => {
          const critical = isCritical(n);
          const group = (n.groupCount ?? 1) > 1 ? n.groupCount! : 0;
          const cat = categoryOf(n);
          const url = typeof n.data?.url === 'string' && /^https?:\/\//.test(n.data.url) ? n.data.url : null;
          const press = () => {
            if (!n.readAt) markRead.mutate([n.id]);
            if (url) { onDone(); open(url); }
          };
          return (
            <div key={n.id} className="header-inbox-entry inbox-card" data-cat={critical ? 'safety' : cat} data-unread={!n.readAt || undefined}>
              <div className="inbox-card-head">
                <span className="inbox-card-icon" aria-hidden="true">{CATEGORY_ICON[critical ? 'safety' : cat]}</span>
                <span className="inbox-card-meta">
                  <span className="inbox-card-kind">{critical ? tr('Emergency') : categoryWord(cat, tr)}{group > 0 && <span className="inbox-card-group"> ×{group}</span>}</span>
                  <span className="header-inbox-row-when">{ago(n.createdAt, tr)}</span>
                </span>
                {!n.readAt && <span className="inbox-card-dot" aria-hidden="true" />}
              </div>
              <button
                type="button"
                className="header-inbox-row"
                data-unread={!n.readAt || undefined}
                data-critical={critical || undefined}
                data-gone={n.cancelledAt || n.supersededById ? true : undefined}
                // Reading it here is reading it — and it opens what it is about
                // (§37.13): the server's `data.url`, the address a push tap opens.
                onClick={press}
              >
                <span className="header-inbox-row-title">{n.title}</span>
                {n.body && <span className="header-inbox-row-body">{n.body}</span>}
              </button>
              <div className="inbox-card-foot">
                <span className="header-inbox-row-tags">
                  {critical && <span className="header-inbox-tag is-critical">{tr('Emergency')}</span>}
                  {n.cancelledAt && <span className="header-inbox-tag">{tr('Withdrawn — no longer applies')}</span>}
                  {!n.cancelledAt && n.supersededById && <span className="header-inbox-tag">{tr('Replaced by a newer notice')}</span>}
                </span>
                <span className="inbox-card-acts">
                  <button
                    type="button"
                    className="header-inbox-del"
                    aria-label={tr('Delete “{title}”', { title: n.title })}
                    onClick={() => remove(n.id)}
                  >
                    <IconTrash />
                  </button>
                  {url && <button type="button" className="inbox-card-open" data-strong={critical || cat === 'safety' || undefined} onClick={press}>{tr('Open')}</button>}
                </span>
              </div>
            </div>
          );
        })}
        {cursor && (
          <button type="button" className="header-inbox-link header-inbox-older" disabled={loadingOlder} onClick={() => void showOlder()}>
            {loadingOlder ? tr('Loading…') : tr('Show older')}
          </button>
        )}
      </div>
      <div className="header-inbox-foot">
        <label className="inbox-expired">
          <input
            type="checkbox"
            role="switch"
            aria-checked={history}
            className="header-inbox-history"
            checked={history}
            onChange={() => setHistory((h) => !h)}
          />
          {tr('Show expired')}
        </label>
        <button type="button" className="header-inbox-link header-inbox-show-all" onClick={() => { onDone(); open(ALL_NOTIFICATIONS()); }}>
          {tr('Show all notifications')}
        </button>
        <button type="button" className="header-inbox-all" onClick={() => { onDone(); open(NOTIFICATIONS()); }}>
          <IconGear />
          {tr('Notification settings')}
        </button>
      </div>
    </>
  );
}

interface MatchLite {
  matchId: string; name: string | null; deleted?: boolean; lastMessage: { body: string; createdAt: string } | null; createdAt: string;
  rave?: { name: string } | null;
}
interface RideLite {
  requestId: string; name: string | null; avatarUrl?: string | null; route: { fromCity: string; toCity: string };
  lastMessage: { body: string; createdAt: string } | null; createdAt: string; rave?: { name: string } | null;
}

/** "DN" — a person's initials for their circle, when there is no picture to show. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1]![0] : '')).toUpperCase() || '·';
}

/** Exported for its test (`messages.test.tsx`); the header renders it in its drop-down. */
export function MessagesPanel({ onDone, unread = 0 }: { onDone: () => void; unread?: number }) {
  const tr = useT();
  const matches = useQuery({
    queryKey: MATCHES_KEY,
    queryFn: () => api.get<{ matches: MatchLite[] }>('/dating/matches'),
    staleTime: 15_000,
    retry: false,
  });
  const rides = useQuery({
    queryKey: RIDE_CHATS_KEY,
    queryFn: () => api.get<{ chats: RideLite[] }>('/ride-chats'),
    staleTime: 15_000,
    retry: false,
  });
  // One list, newest first: the last message, else when the chat began.
  const items = [
    ...(matches.data?.matches ?? []).filter((m) => !m.deleted).map((m) => ({
      key: `m-${m.matchId}`, kind: 'dating' as const, name: m.name ?? tr('Deleted account'), avatar: null as string | null,
      tag: m.rave?.name ?? null, line: m.lastMessage?.body ?? tr('You matched — say hello'), quiet: !m.lastMessage,
      at: m.lastMessage?.createdAt ?? m.createdAt, query: `?match=${encodeURIComponent(m.matchId)}`,
    })),
    ...(rides.data?.chats ?? []).map((c) => ({
      key: `r-${c.requestId}`, kind: 'rides' as const, name: c.name ?? tr('Deleted account'),
      // Only the account's own picture, from our media — never a typed address (a tracking pixel).
      avatar: c.avatarUrl && /^https:\/\//.test(c.avatarUrl) ? c.avatarUrl : null,
      tag: `${c.route.fromCity} → ${c.route.toCity}`, line: c.lastMessage?.body ?? tr('Your ride’s chat is open — say hello'), quiet: !c.lastMessage,
      at: c.lastMessage?.createdAt ?? c.createdAt, query: `?ride=${encodeURIComponent(c.requestId)}`,
    })),
  ].sort((a, b) => b.at.localeCompare(a.at));
  // The design's chips: rides and dating, each only when there is one.
  const [kind, setKind] = useState<'rides' | 'dating' | null>(null);
  const counts = { rides: items.filter((i) => i.kind === 'rides').length, dating: items.filter((i) => i.kind === 'dating').length };
  const shownKind = kind && counts[kind] > 0 ? kind : null;
  const shown = (shownKind ? items.filter((i) => i.kind === shownKind) : items).slice(0, RECENT);
  const loading = matches.isLoading && rides.isLoading;
  // Never "No conversations yet" over a failure (re-validation F2-NEW-4,
  // 2026-10-07): one list down says so beside what did load; both down says
  // nothing loaded. An empty state is only for two lists that answered.
  const failedOne = Boolean(matches.error || rides.error);
  const failedBoth = Boolean(matches.error && rides.error);
  const settled = !matches.isLoading && !rides.isLoading;
  return (
    <>
      <div className="inbox-sheet-head">
        <span className="inbox-sheet-title">
          <h2>{tr('Messages')}</h2>
          {unread > 0 && <span className="inbox-sheet-unread"><span className="inbox-pulse" aria-hidden="true" />{tr('{count} unread', { count: unread })}</span>}
        </span>
        <span className="inbox-sheet-tools">
          <CloseButton onClose={onDone} />
        </span>
      </div>
      {counts.rides > 0 && counts.dating > 0 && (
        <div className="inbox-chips inbox-chips-pad" role="radiogroup" aria-label={tr('Kinds of conversation')}>
          <button type="button" role="radio" aria-checked={!shownKind} className="inbox-chip" data-keep-open onClick={() => setKind(null)}>{tr('All chats')}</button>
          <button type="button" role="radio" aria-checked={shownKind === 'rides'} className="inbox-chip" data-keep-open onClick={() => setKind('rides')}>🚗 {tr('Rides ({count})', { count: counts.rides })}</button>
          <button type="button" role="radio" aria-checked={shownKind === 'dating'} className="inbox-chip" data-keep-open onClick={() => setKind('dating')}>🖤 {tr('Dating ({count})', { count: counts.dating })}</button>
        </div>
      )}
      <div className="inbox-sheet-list">
        {loading && <div className="header-inbox-note">{tr('Loading…')}</div>}
        {failedBoth && <div className="header-inbox-note" role="alert">{tr('Could not load your messages.')}</div>}
        {failedOne && !failedBoth && <div className="header-inbox-note" role="alert">{tr('Some conversations could not load.')}</div>}
        {settled && !failedOne && items.length === 0 && (
          <div className="inbox-empty">
            <span className="inbox-empty-art" aria-hidden="true"><IconChat /><span className="inbox-empty-badge">🎧</span></span>
            <h3 className="inbox-empty-title">{tr('No conversations yet.')}</h3>
            <p className="inbox-empty-text">{tr('Ask for a seat on a rave’s Rideboard — your ride’s chat shows up here.')}</p>
          </div>
        )}
        {shown.map((i) => (
          <button key={i.key} type="button" className="header-inbox-row inbox-chat" data-kind={i.kind} onClick={() => { onDone(); open(messagesUrl(i.query)); }}>
            <span className="inbox-chat-avatar" aria-hidden="true">
              {i.avatar ? <img src={i.avatar} alt="" loading="lazy" /> : <span>{initials(i.name)}</span>}
              <span className="inbox-chat-kind">{i.kind === 'rides' ? '🚗' : '🖤'}</span>
            </span>
            <span className="inbox-chat-body">
              <span className="inbox-chat-top">
                <span className="header-inbox-row-title">{i.name}</span>
                <span className="header-inbox-row-when">{ago(i.at, tr)}</span>
              </span>
              {i.tag && <span className="inbox-chat-tag">{i.tag}</span>}
              <span className="header-inbox-row-body" data-quiet={i.quiet || undefined}>{i.line}</span>
            </span>
          </button>
        ))}
      </div>
      <div className="header-inbox-foot inbox-foot-wide">
        <button type="button" className="header-inbox-all inbox-open-all" onClick={() => { onDone(); open(messagesUrl()); }}>
          {tr('View all')}
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
        </button>
      </div>
    </>
  );
}

/** The sheet's round ✕ (the design's close button). */
function CloseButton({ onClose }: { onClose: () => void }) {
  const tr = useT();
  return (
    <button type="button" className="inbox-close" onClick={onClose} aria-label={tr('Close')}>
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
    </button>
  );
}

/* ---- the categories, as the design draws them ------------------------------ */

const categoryOf = (n: NotificationRow): NotificationCategory => notificationCategory(n.kind, n.data);

export function categoryWord(c: NotificationCategory, tr: ReturnType<typeof useT>): string {
  switch (c) {
    case 'lineup': return tr('Lineup update');
    case 'announcement': return tr('Announcement');
    case 'reminder': return tr('Reminder');
    case 'social': return tr('Social');
    case 'rides': return tr('Rides');
    case 'payment': return tr('Payment');
    case 'account': return tr('Your account');
    case 'safety': return tr('Safety');
    default: return tr('MyRave');
  }
}

const Ico = ({ d }: { d: string }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
);
/** Material Symbols' shapes, drawn — no icon font is downloaded. */
const CATEGORY_ICON: Record<NotificationCategory, JSX.Element> = {
  lineup: <Ico d="M12 7v5l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" />,
  announcement: <Ico d="M3 11v2a1 1 0 0 0 1 1h3l5 4V6L7 10H4a1 1 0 0 0-1 1zM16 9a4 4 0 0 1 0 6M19 6a8 8 0 0 1 0 12" />,
  reminder: <Ico d="M12 8v4l2 2M5 3 2 6M22 6l-3-3M12 21a8 8 0 1 0 0-16 8 8 0 0 0 0 16" />,
  social: <Ico d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8z" />,
  rides: <Ico d="M5 17h14M6 17l1.5-6h9L18 17M7 17v2M17 17v2M8 14h.01M16 14h.01M9 11l1-3h4l1 3" />,
  payment: <Ico d="M3 6h18v12H3zM3 10h18M7 15h3" />,
  account: <Ico d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0" />,
  system: <Ico d="M12 16v-4M12 8h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" />,
  safety: <Ico d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />,
};
const IconSearch = () => <Ico d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.3-4.3" />;
const IconTrash = () => <Ico d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />;
const IconGear = () => <Ico d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />;
const IconBellOff = () => <Ico d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 0 0 3.4 0M9 12h6" />;
const IconChat = () => <Ico d="M4 5h16v11H8l-4 4zM8 9h8M8 12h5" />;

/* ---- a new notification pops up (the user, 2026-10-07) -------------------- */

/** How recent a notification must be to pop up — not a backlog from last week. */
const POP_FRESH_MS = 10 * 60_000;
/** How long a pop-up stays, paused while pointed at. */
const POP_MS = 7000;
/** Ids already popped, on this device — so a reload does not pop them again. */
const POPPED_KEY = 'popped-notifications';

/**
 * Watches the unread count; when it goes UP (or a page opens with unread
 * ones), asks for the newest and pops each fresh, unread one not popped
 * before — its title, and a line of its body.
 */
function useNotificationPopups(count: number | null) {
  const qc = useQueryClient();
  const rave = useRaveScope();
  const prev = useRef<number | null>(null);
  const [items, setItems] = useState<NotificationRow[]>([]);
  useEffect(() => {
    if (count === null) return;
    const was = prev.current;
    prev.current = count;
    if (count <= 0 || (was !== null && count <= was)) return;
    let live = true;
    qc.fetchQuery({
      queryKey: recentKey(rave),
      queryFn: () => api.get<NotificationPage>(recentPath(rave)),
      staleTime: 0,
    }).then((page) => {
      if (!live) return;
      const popped = readJson<string[]>(null, POPPED_KEY, []);
      // Never a withdrawn or replaced one, and never an emergency — that
      // fills the screen instead (`EmergencyScreen.tsx`).
      const fresh = page.notifications.filter((n) =>
        !n.readAt && !n.cancelledAt && !n.supersededById && !isCritical(n)
        && !popped.includes(n.id) && Date.now() - Date.parse(n.createdAt) < POP_FRESH_MS);
      if (fresh.length === 0) return;
      writeJson(null, POPPED_KEY, [...fresh.map((n) => n.id), ...popped].slice(0, 50));
      // Oldest first, at most three on screen.
      setItems((list) => [...list, ...fresh.reverse()].slice(-3));
    }).catch(() => undefined);
    return () => { live = false; };
  }, [count, qc, rave]);
  const dismiss = useCallback((id: string) => setItems((list) => list.filter((n) => n.id !== id)), []);
  return { items, dismiss };
}

function Popup({ n, onOpen, onClose }: { n: NotificationRow; onOpen: () => void; onClose: () => void }) {
  const tr = useT();
  const [paused, setPaused] = useState(false);
  const left = useRef(POP_MS);
  useEffect(() => {
    if (paused) return;
    const started = Date.now();
    const timer = setTimeout(onClose, left.current);
    return () => { clearTimeout(timer); left.current = Math.max(0, left.current - (Date.now() - started)); };
  }, [paused, onClose]);
  return (
    <div
      className="header-inbox-pop"
      role="status"
      aria-live="polite"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <button type="button" className="header-inbox-pop-body" onClick={onOpen}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d={ICON.notifications} />
        </svg>
        <span className="header-inbox-pop-text">
          <span className="header-inbox-pop-title">{n.title}</span>
          {n.body && <span className="header-inbox-pop-line">{n.body}</span>}
        </span>
      </button>
      {/* A ✕ you can SEE (the 0.14.76 walk: "missing X button") — a drawn cross in a ring, not a faint glyph. */}
      <button type="button" className="header-inbox-pop-close" onClick={onClose} aria-label={tr('Close')}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
      </button>
    </div>
  );
}

/* ---- the header's two icons ----------------------------------------------- */

export function HeaderInbox() {
  const tr = useT();
  const unread = useUnread();
  const all = useUnreadAll();
  const rave = useRaveScope();
  const [which, setWhich] = useState<'messages' | 'notifications' | null>(null);
  const wrap = useRef<HTMLSpanElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  // A phone: the design's BOTTOM SHEET; a computer: the dropdown under the grown icon.
  const phone = useMediaQuery(PHONE_QUERY);
  const [width, setWidth] = useState(0);
  const popups = useNotificationPopups(unread ? unread.notifications : null);
  // While an emergency fills the screen, pop-ups wait their turn (§37.82).
  const held = useEmergencyHeld();

  // Escape, or a press anywhere else, closes it — the account menu's rules.
  useEffect(() => {
    if (!which) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setWhich(null); };
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (wrap.current?.contains(t) || sheet.current?.contains(t)) return;
      setWhich(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [which]);

  if (!unread) return null;
  /*
   * Each icon is its own little anchored unit, as the account control is:
   * open, the icon GROWS to the dropdown's width (its name revealed beside
   * it) and the dropdown hangs right under it, the same width — one piece
   * (the user, 2026-10-07). It grows over its neighbours rather than pushing
   * them, so nothing in the header moves; and as wide as fits between the
   * icon's end and the screen's start, measured when it opens.
   */
  const panel = (kind: 'messages' | 'notifications') => (kind === 'messages'
    ? <MessagesPanel onDone={() => setWhich(null)} unread={unread.messages} />
    : <NotificationsPanel
        onDone={() => setWhich(null)}
        here={unread.notifications}
        outside={rave && all ? Math.max(0, all.notifications - unread.notifications) : 0}
      />);
  const item = (kind: 'messages' | 'notifications', label: string, n: number) => {
    const isOpen = which === kind;
    return (
      <span
        className="header-inbox-item"
        data-empty={n <= 0 || undefined}
        data-open={isOpen || undefined}
        style={isOpen && width && !phone ? ({ '--inbox-w': `${width}px` } as React.CSSProperties) : undefined}
      >
        <button
          type="button"
          onClick={(e) => {
            if (isOpen) { setWhich(null); return; }
            if (phone) { setWhich(kind); return; }
            const box = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect();
            const rtl = getComputedStyle(e.currentTarget).direction === 'rtl';
            const room = rtl ? window.innerWidth - box.left - 8 : box.right - 8;
            setWidth(Math.round(Math.max(38, Math.min(PANEL_MAX, room))));
            setWhich(kind);
          }}
          className="header-inbox-btn"
          aria-haspopup="true"
          aria-expanded={isOpen}
          aria-label={n > 0 ? tr('{label}, {count} unread', { label, count: n }) : label}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d={ICON[kind]} />
          </svg>
          <span className="header-inbox-label" aria-hidden="true">{label}</span>
          <Count n={n} />
        </button>
        {isOpen && !phone && (
          <div className="header-inbox-panel" role="dialog" aria-label={label} data-kind={kind}>
            {panel(kind)}
          </div>
        )}
        {isOpen && phone && createPortal(
          <div className="inbox-sheet-layer">
            <div className="inbox-scrim" aria-hidden="true" onClick={() => setWhich(null)} />
            <div ref={sheet} className="header-inbox-panel inbox-sheet" role="dialog" aria-modal="true" aria-label={label} data-kind={kind}>
              <span className="inbox-grab" aria-hidden="true" />
              {panel(kind)}
            </div>
          </div>,
          document.body,
        )}
      </span>
    );
  };
  return (
    <span ref={wrap} className="header-inbox">
      {item('messages', tr('Messages'), unread.messages)}
      {item('notifications', tr('Notifications'), unread.notifications)}
      <FeedEmergency count={all?.notifications ?? 0} open={open} />
      {popups.items.length > 0 && !held && createPortal(
        <div className="header-inbox-pops">
          {popups.items.map((n) => (
            <Popup
              key={n.id}
              n={n}
              onClose={() => popups.dismiss(n.id)}
              // Pressed: the page it is about (the 0.14.76 walk: "when click on it, open the relevant page") —
              // read, as the bell's row does; one with no address opens the bell's dropdown.
              onOpen={() => {
                popups.dismiss(n.id);
                const url = typeof n.data?.url === 'string' && /^https?:\/\//.test(n.data.url) ? n.data.url : null;
                if (!url) { setWhich('notifications'); return; }
                void api.post('/me/notifications/read', { ids: [n.id] }).catch(() => undefined);
                open(url);
              }}
            />
          ))}
        </div>,
        document.body,
      )}
    </span>
  );
}

/** The widest a dropdown (and its grown icon) gets. */
const PANEL_MAX = 400;
/** Where the bottom sheet takes over from the dropdown — the app's phone width. */
const PHONE_QUERY = '(max-width: 879px)';

/** Whether a media query matches, followed as it changes (here, not imported: the shell is the first download). */
function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (cb) => {
      if (typeof window.matchMedia !== 'function') return () => {};
      const mq = window.matchMedia(query);
      mq.addEventListener?.('change', cb);
      return () => mq.removeEventListener?.('change', cb);
    },
    () => typeof window.matchMedia === 'function' && window.matchMedia(query).matches,
  );
}

/** The same two, as rows of the account menu — with their counts, straight to the full page. */
export function InboxMenuItems({ row }: {
  row: (p: { label: string; path: string; count: number; onClick: () => void }) => JSX.Element;
}) {
  const tr = useT();
  const unread = useUnread();
  if (!unread) return null;
  return (
    <>
      {row({ label: tr('Messages'), path: ICON.messages, count: unread.messages, onClick: () => open(messagesUrl()) })}
      {row({ label: tr('Notifications'), path: ICON.notifications, count: unread.notifications, onClick: () => open(ALL_NOTIFICATIONS()) })}
    </>
  );
}
